"use client";

import { useState, useEffect } from "react";
import { useSession, signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { HookActionStatus } from "next-safe-action/hooks";
import { useIsMobile } from "@/hooks/use-mobile";
import Link from "next/link";
import { toast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";

// Data
import { mapStatus } from "./utils";
import { shouldHideNavigation } from "@/lib/constants";
import { AuthView } from "@/components/auth/types";
import { Login, Register, ForgotPassword, OTPVerify } from "@/lib/validations";
import {
  LoginResponse,
  RegisterResponse,
  OTPVerifyResponse,
  ForgotPasswordResponse,
  ResendOTPResponse,
} from "@/types";

// Forms
import { LoginForm } from "./login-form";
import { RegisterForm } from "./register-form";
import { OTPVerificationForm } from "./otp-form";
import { ForgotPasswordForm } from "./forgot-password-form";

// Components
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerTrigger,
  DrawerTitle,
  DrawerDescription,
  DrawerHeader,
} from "@/components/ui/drawer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import Image from "next/image";
import ShineBorder from "@/components/ui/shine-border";
import { COLOURS } from "@/lib/constants";

interface LoginButtonProps {
  onLogin: (values: Login) => Promise<LoginResponse>;
  onRegister: (values: Register) => Promise<RegisterResponse>;
  onVerifyOTP: (values: OTPVerify) => Promise<OTPVerifyResponse>;
  onForgotPassword: (values: ForgotPassword) => Promise<ForgotPasswordResponse>;
  onResendOTP: (values: { email: string }) => Promise<ResendOTPResponse>;
}

interface ContentComponentProps extends LoginButtonProps {
  view: AuthView;
  setView: (view: AuthView) => void;
  email: string;
  password?: string;
  sharedEmail: string;
  sharedPassword: string;
  handleEmailChange: (email: string) => void;
  handlePasswordChange: (password: string) => void;
  loginStatus: HookActionStatus;
  registerStatus: HookActionStatus;
  forgotStatus: HookActionStatus;
  verifyOTPStatus: HookActionStatus;
  resendOTPStatus: HookActionStatus;
  clearPasswordsTrigger?: number;
  isLocked?: boolean;
  lockoutError?: string | null;
  lockoutSeconds?: number;
}

const ContentComponent = ({
  view,
  setView,
  onLogin,
  onRegister,
  onVerifyOTP,
  onForgotPassword,
  onResendOTP,
  ...props
}: ContentComponentProps) => {
  const isMobile = useIsMobile();
  const hideRegister = shouldHideNavigation(); // Hide register in development
  const content = (
    <div className='grid grid-cols-1 md:grid-cols-2 h-full'>
      <div className='hidden md:block h-full overflow-hidden'>
        <Image
          src='https://res.cloudinary.com/djowccubk/image/upload/v1736484790/images/sihp7kppdapqga2kzrpi.webp'
          alt='Login image'
          width={800}
          height={1200}
          quality={100}
          priority
          className='object-cover w-full h-full'
          sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
        />
      </div>
      <div className='p-2 md:p-8 md:pt-14 flex flex-col h-full'>
        <div className='flex flex-col flex-1 h-full overflow-y-auto'>
          {view === "otp" ? (
            <OTPVerificationForm
              email={props.email}
              password={props.password || ""}
              onSubmit={onVerifyOTP}
              status={mapStatus(props.verifyOTPStatus)}
              onViewChange={setView}
              onResendOTP={onResendOTP}
              resendOTPStatus={mapStatus(props.resendOTPStatus)}
            />
          ) : view === "forgotPassword" ? (
            <ForgotPasswordForm
              onSubmit={onForgotPassword}
              status={mapStatus(props.forgotStatus)}
              onViewChange={setView}
            />
          ) : (
            <Tabs
              defaultValue='login'
              className='w-full flex-1 flex flex-col min-h-[55dvh] md:min-h-0'
              value={view === "login" || view === "register" ? view : "login"}
              onValueChange={(value) => {
                if (value === "register" && hideRegister) return; // Don't allow register in development
                setView(value as "login" | "register");
              }}
            >
              <TabsList
                className={`grid w-full ${hideRegister ? "grid-cols-1" : "grid-cols-2"}`}
              >
                <TabsTrigger value='login'>Login</TabsTrigger>
                {!hideRegister && (
                  <TabsTrigger value='register'>Register</TabsTrigger>
                )}
              </TabsList>
              <TabsContent value='login' className='pt-4'>
                <LoginForm
                  onSubmit={onLogin}
                  status={mapStatus(props.loginStatus)}
                  onViewChange={setView}
                  defaultValues={{
                    email: props.sharedEmail,
                    password: props.sharedPassword,
                  }}
                  onEmailChange={props.handleEmailChange}
                  onPasswordChange={props.handlePasswordChange}
                  isLocked={props.isLocked}
                  lockoutError={props.lockoutError}
                  lockoutSeconds={props.lockoutSeconds}
                />
              </TabsContent>
              {!hideRegister && (
                <TabsContent value='register' className='pt-4'>
                  <ScrollArea className='h-[295px] pr-2'>
                    <RegisterForm
                      onSubmit={onRegister}
                      status={mapStatus(props.registerStatus)}
                      defaultValues={{
                        email: props.sharedEmail,
                        password: props.sharedPassword,
                        confirmPassword: "",
                      }}
                      onEmailChange={props.handleEmailChange}
                      onPasswordChange={props.handlePasswordChange}
                      clearPasswordsTrigger={props.clearPasswordsTrigger}
                    />
                  </ScrollArea>
                </TabsContent>
              )}
            </Tabs>
          )}
          {!hideRegister && (
            <p className='text-sm text-muted-foreground mt-auto pt-6'>
              By clicking continue, you agree to our{" "}
              <Link href='/terms-conditions' className='underline'>
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link href='/privacy-policy' className='underline'>
                Privacy Policy
              </Link>
              .
            </p>
          )}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return <div className='w-full h-full'>{content}</div>;
  }

  return (
    <ShineBorder
      className='w-full h-full p-0 m-0 rounded-2xl transform-gpu dark:[box-shadow:0_-20px_80px_-20px_#8686f01f_inset]'
      color={[
        "#2d2d2d",
        "#2d2d2d",
        COLOURS.primary,
        "#441d00",
        "#2d2d2d",
        "#2d2d2d",
      ]}
      duration={60}
      borderRadius={12}
    >
      {content}
    </ShineBorder>
  );
};

export default function LoginButton({
  onLogin,
  onRegister,
  onVerifyOTP,
  onForgotPassword,
  onResendOTP,
}: LoginButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<AuthView>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sharedEmail, setSharedEmail] = useState("");
  const [sharedPassword, setSharedPassword] = useState("");
  const [loginStatus, setLoginStatus] = useState<HookActionStatus>("idle");
  const [registerStatus, setRegisterStatus] =
    useState<HookActionStatus>("idle");
  const [verifyOTPStatus, setVerifyOTPStatus] =
    useState<HookActionStatus>("idle");
  const [resendOTPStatus, setResendOTPStatus] =
    useState<HookActionStatus>("idle");
  const [clearPasswordsTrigger, setClearPasswordsTrigger] = useState(0);

  // Lockout state
  const [lockedUntil, setLockedUntil] = useState<Date | null>(null);
  const [lockoutError, setLockoutError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number>(0); // ms

  const isMobile = useIsMobile();
  const { status } = useSession();
  const router = useRouter();

  // On mount, check localStorage for lockout
  useEffect(() => {
    const stored = localStorage.getItem("loginLockedUntil");
    if (stored) {
      const until = new Date(stored);
      if (until > new Date()) {
        setLockedUntil(until);
        setLockoutError(
          `Account locked. Try again in ${Math.ceil((until.getTime() - Date.now()) / 1000)} seconds.`
        );
      } else {
        localStorage.removeItem("loginLockedUntil");
      }
    }
  }, []);

  // Timer for countdown
  useEffect(() => {
    if (!lockedUntil) return;

    function update() {
      const now = new Date();
      const diff = lockedUntil
        ? Math.max(0, lockedUntil.getTime() - now.getTime())
        : 0;
      setRemaining(diff);

      // Update lockout error message with current countdown
      if (diff > 0) {
        const seconds = Math.ceil(diff / 1000);
        setLockoutError(`Account locked. Try again in ${seconds} seconds.`);
      } else {
        // Clear lockout when timer expires
        setLockedUntil(null);
        setLockoutError(null);
        localStorage.removeItem("loginLockedUntil");
      }
    }

    // Initial update
    update();

    // Set up interval
    const interval = setInterval(update, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [lockedUntil]);

  // Close dialog when session is active
  useEffect(() => {
    if (status === "authenticated" && isOpen) {
      setIsOpen(false);

      // Force an immediate refresh to update the session state everywhere
      router.refresh();

      // Optionally, reload the page after a short delay if the refresh isn't sufficient
      // This ensures all components see the updated session
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
  }, [status, isOpen, router]);

  // If authenticated, don't render the button
  if (status === "authenticated") {
    return null;
  }

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setView("login");
    }
  };

  const handleLogin = async (values: Login) => {
    // If locked, block login and show toast
    if (lockedUntil && new Date() < lockedUntil) {
      const seconds = Math.ceil(remaining / 1000);
      toast({
        title: "Account Locked",
        description: `Account locked. Try again in ${seconds} seconds.`,
        variant: "destructive",
      });
      return { error: `Account locked. Try again in ${seconds} seconds.` };
    }

    try {
      setLoginStatus("executing");
      const response = await onLogin(values);

      // Handle lockout response
      if (response.lockedUntil) {
        const until = new Date(response.lockedUntil);
        setLockedUntil(until);
        setLockoutError(response.error || "Account locked.");
        localStorage.setItem("loginLockedUntil", response.lockedUntil);

        // Don't show toast here - it's already handled by error handling below
        setLoginStatus("hasErrored");
        return response;
      }

      if (
        response.success === "Confirmation Email Sent!" &&
        "email" in response &&
        "password" in response &&
        response.email &&
        response.password
      ) {
        setEmail(response.email);
        setPassword(response.password);
        setView("otp");
        setLoginStatus("hasSucceeded");
        toast({
          title: "Verification Required",
          description: "Please check your email for the verification code.",
          variant: "default",
        });
        return response;
      } else if (response.error) {
        // Handle banned user redirect
        if (response.redirect) {
          setLoginStatus("hasErrored");
          toast({
            title: "Access Denied",
            description: response.error,
            variant: "destructive",
          });
          setIsOpen(false); // Close the dialog
          router.push(response.redirect);
          return { error: response.error };
        }

        setLoginStatus("hasErrored");
        toast({
          title: "Error",
          description: response.error,
          variant: "destructive",
        });
        return { error: response.error };
      } else if (
        response.success === "Temporary password detected" &&
        response.requirePasswordReset &&
        response.resetToken
      ) {
        setLoginStatus("hasSucceeded");
        toast({
          title: "Password Reset Required",
          description: "Please set a new password for your account.",
          variant: "default",
        });

        // Redirect to password-reset page with the token
        const resetUrl = `/password-reset?token=${response.resetToken}`;
        router.push(resetUrl);

        return response;
      } else if (
        response.success === "Login successful" &&
        "credentials" in response &&
        response.credentials
      ) {
        setLoginStatus("hasSucceeded");

        // Sign in with the credentials
        const signInResult = await signIn("credentials", {
          email: response.credentials.email,
          password: response.credentials.password,
          redirect: false,
        });

        if (signInResult?.ok) {
          // Show success toast
          toast({
            title: "Success",
            description: "Logged in successfully!",
            variant: "success",
          });

          // Close the dialog
          setIsOpen(false);

          // Use router.refresh() to update the session state without a full page refresh
          router.refresh();

          // Force a complete reload after a small delay to ensure all components
          // see the updated session state
          setTimeout(() => {
            window.location.reload();
          }, 50);

          return response;
        }
      }

      return response;
    } catch (error) {
      console.error("Login error:", error);
      setLoginStatus("hasErrored");
      toast({
        title: "Error",
        description: "An unexpected error occurred during login.",
        variant: "destructive",
      });
      // Reset status after showing error
      setTimeout(() => {
        setLoginStatus("idle");
      }, 100);
      return { error: "An unexpected error occurred" };
    }
  };

  const handleRegister = async (values: Register) => {
    try {
      setRegisterStatus("executing");
      const response = await onRegister(values);
      if (response.success === "OTP Sent!") {
        setEmail(values.email);
        setPassword(values.password);
        setView("otp");
        setRegisterStatus("hasSucceeded");
        toast({
          title: "Registration Started",
          description: "Please check your email for the verification code.",
          variant: "default",
        });
      } else if (response.success === "OTP resent") {
        setEmail(values.email);
        setPassword(values.password);
        setView("otp");
        setRegisterStatus("hasSucceeded");
        toast({
          title: "Verification Required",
          description: "A new verification code has been sent to your email.",
          variant: "default",
        });
      } else if (response.error === "Email already in use") {
        setRegisterStatus("hasErrored");
        setClearPasswordsTrigger((v) => v + 1);
        toast({
          title: "Registration Failed",
          description:
            "This email is already registered. Please try logging in instead.",
          variant: "destructive",
        });
      } else if (response.error) {
        setRegisterStatus("hasErrored");
        toast({
          title: "Registration Failed",
          description: response.error,
          variant: "destructive",
        });
      }
      return response;
    } catch (error) {
      console.error("Register error:", error);
      setRegisterStatus("hasErrored");
      toast({
        title: "Error",
        description: "An unexpected error occurred during registration.",
        variant: "destructive",
      });
      return { error: "An unexpected error occurred" };
    }
  };

  const handleVerifyOTP = async (values: OTPVerify) => {
    try {
      setVerifyOTPStatus("executing");
      const response = await onVerifyOTP(values);

      if (response.error) {
        setVerifyOTPStatus("hasErrored");
        toast({
          title: "Error",
          description: response.error,
          variant: "destructive",
        });
        return { error: response.error };
      } else if (
        response.success &&
        "credentials" in response &&
        response.credentials
      ) {
        setVerifyOTPStatus("hasSucceeded");
        toast({
          title: "Success",
          description: "Email verified successfully!",
          variant: "success",
        });

        // Sign in with the credentials
        const signInResult = await signIn("credentials", {
          email: response.credentials.email,
          password: response.credentials.password,
          redirect: false,
        });

        if (signInResult?.ok) {
          // Close the dialog
          setIsOpen(false);

          // Use router.refresh() to update the session state without a full page refresh
          router.refresh();

          // Force a complete reload after a small delay to ensure all components
          // see the updated session state
          setTimeout(() => {
            window.location.reload();
          }, 50);

          return response;
        }
      }

      return response;
    } catch (error) {
      console.error("OTP verification error:", error);
      setVerifyOTPStatus("hasErrored");
      toast({
        title: "Error",
        description: "An unexpected error occurred during verification.",
        variant: "destructive",
      });
      // Reset status after showing error
      setTimeout(() => {
        setVerifyOTPStatus("idle");
      }, 100);
      return { error: "An unexpected error occurred" };
    }
  };

  const handleResendOTP = async (values: { email: string }) => {
    try {
      setResendOTPStatus("executing");
      const response = await onResendOTP(values);
      setResendOTPStatus("hasSucceeded");
      return response;
    } catch (error) {
      console.error("Resend OTP error:", error);
      setResendOTPStatus("hasErrored");
      return { error: "An unexpected error occurred" };
    }
  };

  const commonProps = {
    view,
    setView,
    email,
    password,
    sharedEmail,
    sharedPassword,
    handleEmailChange: setSharedEmail,
    handlePasswordChange: setSharedPassword,
    loginStatus,
    registerStatus,
    forgotStatus: "idle" as HookActionStatus,
    verifyOTPStatus,
    resendOTPStatus,
    onLogin: handleLogin,
    onRegister: handleRegister,
    onVerifyOTP: handleVerifyOTP,
    onForgotPassword,
    onResendOTP: handleResendOTP,
    clearPasswordsTrigger,
    isLocked: lockedUntil !== null && new Date() < lockedUntil,
    lockoutError: lockoutError,
    lockoutSeconds: Math.ceil(remaining / 1000),
  };

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>
          <Button>Login</Button>
        </DrawerTrigger>
        <DrawerContent className='z-[999] bg-background'>
          <DrawerHeader className='px-4'>
            <DrawerTitle className='text-center'>Authentication</DrawerTitle>
            <DrawerDescription className='text-center'>
              Login or register for an account
            </DrawerDescription>
          </DrawerHeader>
          <div className='flex-1 overflow-y-auto p-6'>
            <ContentComponent {...commonProps} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Login</Button>
      </DialogTrigger>
      <DialogContent className='sm:max-w-[800px] p-0 h-[500px] bg-opacity-25 backdrop-blur-sm bg-black rounded-2xl overflow-hidden border-0'>
        <DialogTitle className='sr-only'>Authentication</DialogTitle>
        <DialogDescription className='sr-only'>
          Login or register for an account
        </DialogDescription>
        <ContentComponent {...commonProps} />
      </DialogContent>
    </Dialog>
  );
}
